import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api_auth } from "../../services/api_config";
import { useForm } from "react-hook-form";
import { Divider } from "@mui/material";
import InputField from "../InputField/InputField";
import toast from "react-hot-toast";
import Buttons from "../../utils/Buttons";
import { encryptPassword } from "../../utils/encryptPassword";

const ResetPassword = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onTouched",
  });

  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const password = watch("password");

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card text-center">
          <h1 className="auth-title text-red-500 mb-4">Invalid Reset Link</h1>
          <p className="auth-subtitle mb-4">
            This password reset link is invalid or has expired.
          </p>
          <Link className="auth-link" to="/forgot-password">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  const handleResetPassword = async (data) => {
    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append("token", token);
      formData.append("newPassword", await encryptPassword(data.password));
      await api_auth.post("/auth/public/reset-password", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      toast.success("Password reset successful!");
      reset();
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      const message =
        error.response?.data?.message || "Error resetting password. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <form onSubmit={handleSubmit(handleResetPassword)} className="auth-card">
        <div>
          <h1 className="auth-title">Reset Your Password</h1>
          <p className="auth-subtitle">Enter and confirm your new password</p>
        </div>
        <Divider className="auth-divider font-semibold pb-4 mt-4" />

        <div className="flex flex-col gap-2 mt-4">
          <InputField
            label="New Password"
            required
            id="password"
            type="password"
            message="*Password is required"
            placeholder="Enter new password"
            register={register}
            errors={errors}
            min={6}
          />

          <div className="flex flex-col gap-1">
            <label htmlFor="confirmPassword" className="input-label">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              placeholder="Confirm new password"
              className={`input-field ${
                errors.confirmPassword?.message ? "input-field-error" : ""
              }`}
              {...register("confirmPassword", {
                required: { value: true, message: "*Confirm password is required" },
                validate: (value) =>
                  value === password || "Passwords do not match",
              })}
            />
            {errors.confirmPassword?.message && (
              <p className="text-sm font-semibold text-red-500 mt-0">
                {errors.confirmPassword.message}*
              </p>
            )}
          </div>
        </div>

        <Buttons
          disabled={loading}
          onClickhandler={() => {}}
          className="btn-primary w-full my-4"
          type="text"
        >
          {loading ? <span>Loading...</span> : "Reset Password"}
        </Buttons>
        <p className="auth-text">
          <Link className="auth-link" to="/login">Back To Login</Link>
        </p>
      </form>
    </div>
  );
};

export default ResetPassword;
